import { Suspense, useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { EcosuiteErrorBoundary } from "@/components/error-boundary";
import RootUserLayout from "../layout";
import {
  auditLogsQueryOptions,
  organizationAuditOperationType,
} from "../service/audit";
import { AuditTable } from "./components/audit-table";
import { Card, CardContent } from "@/components/ui/card";
import { AlertCircle } from "lucide-react";
import { z } from "zod";

const DEFAULT_PAGE_SIZE = 10;

function AuditContent() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [clientFilters, setClientFilters] = useState<{
    operationType?: z.infer<typeof organizationAuditOperationType>;
    updatedBy?: string;
  }>({});

  // Only pass pagination parameters to the server
  const queryParams = { page, pageSize };

  const { data: auditData } = useSuspenseQuery(
    auditLogsQueryOptions(queryParams),
  );

  const handlePaginationChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
  };

  const handleFilterChange = (columnFilters: Record<string, unknown>) => {
    const newFilters: typeof clientFilters = {};

    // Process filters but don't send to server
    for (const [key, value] of Object.entries(columnFilters)) {
      if (key === "operationType" && Array.isArray(value) && value.length > 0) {
        newFilters.operationType = value[0] as z.infer<
          typeof organizationAuditOperationType
        >;
      }

      if (key === "updatedBy" && typeof value === "string") {
        newFilters.updatedBy = value;
      }
    }

    setClientFilters(newFilters);
    // Reset to first page when filters change
    setPage(1);
  };

  // Apply client-side filtering
  let filteredData = { ...auditData };

  // Only filter the logs array, keep pagination info as is
  if (clientFilters.operationType || clientFilters.updatedBy) {
    const filteredLogs = auditData.logs.filter((log) => {
      // Check operation type filter
      if (
        clientFilters.operationType &&
        log.operationType !== clientFilters.operationType
      ) {
        return false;
      }

      // Check updatedBy filter - now check against updatedByDisplayName
      if (
        clientFilters.updatedBy &&
        !log.updatedByDisplayName.includes(clientFilters.updatedBy)
      ) {
        return false;
      }

      return true;
    });

    // Return the filtered logs with server pagination info
    filteredData = {
      ...auditData,
      logs: filteredLogs,
    };
  }

  return (
    <AuditTable
      data={filteredData}
      onPaginationChange={handlePaginationChange}
      onFilterChange={handleFilterChange}
    />
  );
}

export default function AuditLogPage() {
  return (
    <RootUserLayout
      pageTitle="Audit Logs"
      pageDescription="View system audit trails for organization changes."
    >
      <Suspense
        fallback={
          <LoadingScreen
            text="Loading Audit Logs..."
            className="h-[calc(100vh-180px)]"
          />
        }
      >
        <EcosuiteErrorBoundary>
          <AuditContent />
        </EcosuiteErrorBoundary>
      </Suspense>
    </RootUserLayout>
  );
}
